import consola from 'consola';

function abortFailedLogin(csrfCookie) {
  if (csrfCookie) {
    csrfCookie.value = null;
  }

  const sessionStore = useSessionStore();
  const { clearLoginCookies } = useLoginCookies();
  const { useUrl } = useUrls();

  clearLoginCookies();
  sessionStore.resetSessionStore();

  return navigateTo(useUrl.getStaticBaseURL(), { external: true });
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (!(process.server && to?.query?.code)) {
    return;
  }

  // The following code runs on Login, but not on Logout,
  // beacuse on Logout the "to?.query?.code" is not present.

  const appConfig = useRuntimeConfig();

  const csrfCookie = useCookie(appConfig.public.LOGIN_CSRF_PROTECTION_COOKIE, { domain: appConfig.public.COOKIE_SESSION_DOMAIN });

  const csrfExpected = csrfCookie?.value || '';

  if (!(csrfExpected && to?.query?.state && csrfExpected === to?.query?.state)) {
    // CSRF protection check failed.
    consola.warn('### Session error during CSRF protection check');
    return abortFailedLogin(csrfCookie);
  }

  csrfCookie.value = null;

  const nuxtApp = useNuxtApp();
  const locale = nuxtApp.$i18n.locale;
  const $api = nuxtApp.$api;
  const sessionStore = useSessionStore();
  const { useUrl } = useUrls();

  const url = `https://${appConfig.public.AUTH0_DOMAIN}/oauth/token`;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: appConfig.public.AUTH0_CLIENTID,
    client_secret: appConfig.AUTH0_CLIENT_SECRET,
    code: to.query.code,
    redirect_uri: useUrl.getAuth0CallbackPageUrl(), // TODO this value should be unique
  });

  let accessToken;

  try {
    const {
      id_token,
      access_token,
      refresh_token,
      expires_in,
    } = await $fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });

    accessToken = access_token;

    const session = {
      idToken: id_token,
      apiToken: {
        accessToken: access_token,
        refreshToken: refresh_token,
        expiresIn: expires_in,
      },
    };

    await sessionStore.setSession(session);

    // Set cookies after session is successfully set (refresh is HttpOnly — FBF-982)
    const {
      getLoginCookieOptions,
      getRefreshCookieOptions,
      LOGIN_TOKEN_COOKIE,
      LOGIN_ACCESS_TOKEN_COOKIE,
      LOGIN_REFRESH_TOKEN_COOKIE,
      LOGIN_EXPIRES_IN_COOKIE,
    } = useLoginCookies();

    const idTokenMaxAge = 8 * 60 * 60;
    const accessTokenMaxAge = Number.parseInt(session.apiToken?.expiresIn) || 3600;
    const refreshTokenMaxAge = 8640000;

    const userLoginIdToken = useCookie(LOGIN_TOKEN_COOKIE, { ...getLoginCookieOptions(idTokenMaxAge) });
    const userLoginAccessToken = useCookie(LOGIN_ACCESS_TOKEN_COOKIE, { ...getLoginCookieOptions(accessTokenMaxAge) });
    const userLoginRefreshToken = useCookie(LOGIN_REFRESH_TOKEN_COOKIE, { ...getRefreshCookieOptions(refreshTokenMaxAge) });
    const userLoginExpiresInToken = useCookie(LOGIN_EXPIRES_IN_COOKIE, { ...getLoginCookieOptions(accessTokenMaxAge) });

    userLoginIdToken.value = session.idToken;
    userLoginAccessToken.value = session.apiToken.accessToken;
    userLoginRefreshToken.value = session.apiToken.refreshToken;
    userLoginExpiresInToken.value = session.apiToken.expiresIn;

    // Do not expose the refresh token to the client payload / Pinia hydration
    session.apiToken.refreshToken = null;
    sessionStore.setApiToken(session.apiToken);
  } catch (err) {
    consola.error(`### Session error during authorization code exchange: ${err?.message || 'Unknown error'}`);

    // @TODO: display a message to the user that login was not successful
    return abortFailedLogin(csrfCookie);
  }

  // Profile enrichment is non-fatal — do not wipe a successful Auth0 session.
  const userId = sessionStore.getSessionUser()?.customerId;

  if (!userId) {
    return;
  }

  try {
    const userDetails = await $api.hwapi(
      `getUserDetails${userId}`,
      `users/${userId}/`,
      {
        'Accept-Language': locale?.value,
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': accessToken,
      },
      {},
      {},
    );

    sessionStore.setBirthday(userDetails?.dateOfBirth);
    sessionStore.setNationality(userDetails?.nationality);
  } catch (err) {
    consola.error(`### Session error fetching user profile after login: ${err?.message || 'Unknown error'}`);
  }
});
